import { useEffect, useState } from "react";
import { cn } from "@/cn";
import { parseVerdictScore, resolveJobScore, scoreTone } from "@/format";
import { useUpdateJob } from "@/hooks/useJobs";

const TONE_BG: Record<string, string> = {
  good: "bg-emerald-500/10 text-emerald-400",
  warn: "bg-amber-500/10 text-amber-400",
  bad: "bg-red-500/10 text-red-400",
  muted: "bg-zinc-500/10 text-muted-foreground",
};

interface JobScoreControlProps {
  jobId: string;
  job: {
    score?: number;
    fitScore?: number;
    evaluation?: string;
    research?: string;
  };
  className?: string;
}

export function JobScoreControl({ jobId, job, className }: JobScoreControlProps) {
  const updateJob = useUpdateJob(jobId);
  const resolvedScore = resolveJobScore(job);
  const [draft, setDraft] = useState(resolvedScore !== undefined ? String(resolvedScore) : "");
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    setDraft(resolvedScore !== undefined ? String(resolvedScore) : "");
  }, [resolvedScore, job.score, job.evaluation, job.research]);

  const parsedFromText =
    parseVerdictScore(job.evaluation ?? "") ??
    parseVerdictScore(job.research ?? "");
  const hasTextSource = Boolean(job.evaluation?.trim() || job.research?.trim());
  const showExtractHint =
    parsedFromText !== undefined &&
    (job.score === undefined || job.score !== parsedFromText);

  function handleExtract() {
    if (parsedFromText === undefined) {
      setMessage("No score found in evaluation or research text.");
      return;
    }
    setDraft(String(parsedFromText));
    setMessage(`Detected ${parsedFromText}/5 in the output. Click Save to apply.`);
  }

  function handleSave() {
    setMessage(null);
    const trimmed = draft.trim();
    if (!trimmed) {
      updateJob.mutate(
        { score: undefined } as never,
        {
          onSuccess: () => setMessage("Score cleared."),
          onError: () => setMessage("Failed to save score."),
        },
      );
      return;
    }

    const value = parseFloat(trimmed);
    if (!Number.isFinite(value) || value < 0 || value > 5) {
      setMessage("Enter a score between 0 and 5.");
      return;
    }

    updateJob.mutate(
      { score: Math.round(value * 10) / 10 } as never,
      {
        onSuccess: () => setMessage("Score saved."),
        onError: () => setMessage("Failed to save score."),
      },
    );
  }

  return (
    <div className={cn("rounded-lg border border-border bg-card p-4", className)}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Fit score
          </h3>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Used in the pipeline list and stage view. Override here if the AI did not propagate it.
          </p>
        </div>
        {resolvedScore !== undefined && (
          <span
            className={cn(
              "rounded-full px-3 py-1 font-mono text-sm font-semibold",
              TONE_BG[scoreTone(String(resolvedScore))],
            )}
          >
            {resolvedScore}/5
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            Manual score (0–5)
          </span>
          <input
            type="number"
            min={0}
            max={5}
            step={0.1}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="e.g. 3.5"
            className="w-28 rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand"
          />
        </label>
        <button
          type="button"
          onClick={handleSave}
          disabled={updateJob.isPending}
          className="rounded-md bg-brand px-3 py-2 text-xs font-medium text-brand-foreground transition hover:opacity-90 disabled:opacity-50"
        >
          {updateJob.isPending ? "Saving…" : "Save score"}
        </button>
        {hasTextSource && (
          <button
            type="button"
            onClick={handleExtract}
            disabled={updateJob.isPending}
            className="rounded-md border border-border px-3 py-2 text-xs font-medium text-foreground transition hover:bg-muted/20 disabled:opacity-50"
          >
            Extract from output
          </button>
        )}
      </div>

      {showExtractHint && (
        <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
          Found {parsedFromText}/5 in the AI output but it is not saved on this job yet. Use
          &ldquo;Extract from output&rdquo; then Save.
        </p>
      )}

      {message && (
        <p className="mt-2 text-xs text-muted-foreground">{message}</p>
      )}
    </div>
  );
}
